import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  unFormalizedZipSelectors,
  getAllUnFormalizedZips,
  getUnFormalizedZipById,
  createUnFormalizedZip,
  updateUnFormalizedZip,
  deleteUnFormalizedZip,
  unFormalizedZipActions,
} from 'entities/unFormalizedZip';
import { userSelectors } from 'entities/user';
import { UnFormalizedZipRequestDto, UnFormalizedZipResponseDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';

export const useUnFormalizedZip = () => {
  const dispatch = useAppDispatch();
  const unFormalizedZips = useAppSelector(unFormalizedZipSelectors.getUnFormalizedZips);
  const isLoading = useAppSelector(unFormalizedZipSelectors.getUnFormalizedZipsLoading);
  const detail = useAppSelector(unFormalizedZipSelectors.getUnFormalizedZipDetail);
  const detailLoading = useAppSelector(unFormalizedZipSelectors.getUnFormalizedZipDetailLoading);
  const hasAdminRole = useAppSelector(userSelectors.hasAdminRole);
  const user = useAppSelector(userSelectors.getUser);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const [detailError, setDetailError] = useState<unknown>(null);

  const loadUnFormalizedZips = useCallback(async (status?: UnFormalizedZipResponseDto.status) => {
    try {
      showSpinner('Загрузка неформализованных ЗИП...');
      const company = hasAdminRole ? undefined : user?.company;
      const result = await dispatch(getAllUnFormalizedZips({ company, status })).unwrap();
      return result;
    } catch (error) {
      setDetailError(error);
      throw error;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner, hasAdminRole, user]);

  const createNew = useCallback(async (data: UnFormalizedZipRequestDto) => {
    try {
      showSpinner('Создание неформализованного ЗИП...');
      const result = await dispatch(createUnFormalizedZip(data)).unwrap();
      return result;
    } catch (error) {
      setDetailError(error);
      throw error;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const updateExisting = useCallback(async (id: number, data: UnFormalizedZipRequestDto) => {
    try {
      showSpinner('Обновление неформализованного ЗИП...');
      const result = await dispatch(updateUnFormalizedZip({ id, data })).unwrap();
      return result;
    } catch (error) {
      setDetailError(error);
      throw error;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const deleteById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление неформализованного ЗИП...');
      const result = await dispatch(deleteUnFormalizedZip(id)).unwrap();
      return result;
    } catch (error) {
      setDetailError(error);
      throw error;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailError(null);
    try {
      showSpinner('Загрузка данных неформализованного ЗИП...');
      const detailData = await dispatch(getUnFormalizedZipById(id)).unwrap();
      return { success: true, detail: detailData } as const;
    } catch (error) {
      setDetailError(error);
      return { success: false, error } as const;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const clearDetail = useCallback(() => {
    dispatch(unFormalizedZipActions.dropDetailData());
    setDetailError(null);
  }, [dispatch]);

  return {
    unFormalizedZips,
    isLoading,
    loadUnFormalizedZips,
    createNew,
    updateExisting,
    deleteById,
    detail,
    detailLoading,
    detailError,
    loadDetail,
    clearDetail,
  };
};
